/**
 * CorrectionNotice — visible, dated correction notice (corrections-desk R3).
 *
 * Quiet-register trust surface consistent with /policies#corrections
 * ("We update the record. We never scrub it."). Server-rendered, no client JS.
 * Renders NOTHING when the doc has no correction, so uncorrected content is
 * byte-identical to before this component existed.
 *
 * Two flavors (from buildCorrectionView):
 *   - CORRECTED  — "Correction · <date>" + the reader-facing note.
 *   - SUPERSEDED — "Superseded · <date>" + a forward pointer to the replacement
 *                  (server-resolved from correction.supersededBy).
 * When earlier correction hops exist in the transitionLog, their dates are shown so
 * the repair history isn't erased by a later supersede overwriting the field.
 */
import Link from 'next/link';
import type { Correction, TransitionHop } from '../../../shared/types/breaking-news';
import { buildCorrectionView, type SupersededLink } from '@/lib/corrections';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // UTC: a correction date is an editorial fact ("Correction, July 12"), not a viewer-local
  // event time — render it deterministically so SSR and every client agree on the calendar day.
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

interface CorrectionNoticeProps {
  correction?: Correction | null;
  transitionLog?: TransitionHop[] | null;
  /** Server-resolved forward pointer for the superseded flavor (from supersededBy). */
  supersededLink?: SupersededLink | null;
}

export default function CorrectionNotice({
  correction,
  transitionLog,
  supersededLink,
}: CorrectionNoticeProps) {
  const view = buildCorrectionView(correction, transitionLog);
  if (!view) return null;

  const isSuperseded = view.kind === 'superseded';
  const heading = isSuperseded ? 'Superseded' : 'Correction';
  const dateLabel = formatDate(view.at);

  return (
    <aside
      role="note"
      aria-label={isSuperseded ? 'Supersession notice' : 'Correction notice'}
      className="my-6 rounded-lg border-[0.5px] border-scout-gold/40 bg-scout-gold/[0.06] px-5 py-4"
    >
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 font-mono text-[11px] uppercase tracking-[0.14em] text-scout-gold">
        <span>{heading}</span>
        {dateLabel && (
          <>
            <span className="text-scout-gold/50">·</span>
            <time dateTime={view.at ?? undefined} className="text-scout-gold/80">
              {dateLabel}
            </time>
          </>
        )}
      </div>

      {view.note && (
        <p className="text-[15px] leading-[1.6] text-parchment/90">{view.note}</p>
      )}

      {isSuperseded && (
        <p className="mt-2 text-[14px] leading-[1.6] text-parchment-dim">
          This article was superseded by{' '}
          {supersededLink?.href ? (
            <Link
              href={supersededLink.href}
              className="text-scout-gold underline underline-offset-2 transition-colors hover:text-scout-gold/80"
            >
              {supersededLink.title || 'an updated report'}
            </Link>
          ) : (
            <span className="text-parchment">an updated report</span>
          )}
          . The original is kept here, on the record, pointing forward.
        </p>
      )}

      {view.priorDates.length > 0 && (
        <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.12em] text-parchment-dim/70">
          {view.priorDates.length === 1 ? 'Earlier correction' : 'Earlier corrections'}:{' '}
          {view.priorDates.map(formatDate).filter(Boolean).join(' · ')}
        </p>
      )}
    </aside>
  );
}
